import { useState, useCallback, useContext } from 'react'
import { Card, Chip, Grid, Layout } from '@enterprise-ui/canvas-ui-react'
import {
  COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
  COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS,
  DVS_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS,
  COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
  COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
  COST_CHANGE_INSIGHTS_MISC_OPTIONS,
  COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS,
} from '../../globalConfig/constants'
import { CostChangeInsightsGlobalContext } from '../../globalState/CostChangeInsights'
import { CostChangeInsightsPageContext } from './pageState'
import { FilterPopup } from '../../globalComponents/FilterPopup'
import {
  constructInitialValueStates,
  stripItemHubPath,
} from './helperFunctions'
import { getLocation } from '@praxis/runtime-utils/getLocation'

export const CostChangeInsightsAppliedFilters = () => {
  const location = getLocation()
  const [openPopup, setOpenPopup] = useState(false)
  const [popupFieldConstant, setPopupFieldConstant] = useState()
  const [globalState, globalDispatch] = useContext(
    CostChangeInsightsGlobalContext,
  )
  const [pageState, pageDispatch] = useContext(CostChangeInsightsPageContext)
  const pathname = stripItemHubPath(location.pathname)

  const { searchCriteria } = globalState[pathname]
  const allFieldsByVariableNames = Object?.values({
    ...COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
    ...COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
    ...COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
    ...COST_CHANGE_INSIGHTS_MISC_OPTIONS,
  }).reduce((self, header) => ({ ...self, [header.variableName]: header }), {})

  const ALL_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS = {
    ...COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS,
    ...DVS_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS,
  }

  const onFilterPopupOpen = (fieldConstant) => {
    setPopupFieldConstant(fieldConstant)
    setOpenPopup(true)
  }

  const onFilterPopupClose = useCallback(() => {
    setOpenPopup(false)
  }, [])

  const removeValue = (
    valueToRemove,
    fieldVariableName,
    currentValues,
    resetToDefault,
  ) => {
    if (resetToDefault) {
      if (
        !Object.values(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).some(
          (itemOption) => itemOption.variableName === fieldVariableName,
        )
      ) {
        const initialValueStates = constructInitialValueStates()
        pageDispatch({
          type: 'update_value_states_by_field',
          field: allFieldsByVariableNames[fieldVariableName],
          value: initialValueStates[fieldVariableName],
        })
      }
      globalDispatch({
        type: 'update_criteria_by_field',
        field: allFieldsByVariableNames[fieldVariableName],
        value: COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS[fieldVariableName],
        pathname: pathname,
      })
    } else {
      const updatedGlobalValues = currentValues.filter(
        (value) => value !== valueToRemove,
      )
      globalDispatch({
        type: 'update_criteria_by_field',
        field: allFieldsByVariableNames[fieldVariableName],
        value: updatedGlobalValues,
        pathname: pathname,
      })
      if (
        !Object.values(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).some(
          (itemOption) => itemOption.variableName === fieldVariableName,
        )
      ) {
        const updatedPageValues = pageState.valueStates[
          fieldVariableName
        ].filter((option) => option.value !== valueToRemove)
        pageDispatch({
          type: 'update_value_states_by_field',
          field: allFieldsByVariableNames[fieldVariableName],
          value: updatedPageValues,
        })
      }
    }
  }

  const filterGroupHasValues = (filterGroup) => {
    return Object.values(filterGroup).some((filterField) => {
      return Array.isArray(searchCriteria?.[filterField.variableName])
        ? searchCriteria?.[filterField.variableName].length
        : searchCriteria?.[filterField.variableName]
    })
  }

  const getFilterPopupLocationText = () => {
    const costLocationGroupTypeCount =
      searchCriteria?.[
        COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
          .variableName
      ].length
    if (costLocationGroupTypeCount > 1) {
      return `${costLocationGroupTypeCount} ${ALL_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[searchCriteria?.[COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE.variableName]]?.pluralLabel}`
    } else {
      return `${costLocationGroupTypeCount} ${ALL_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[searchCriteria?.[COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE.variableName]]?.label}`
    }
  }

  return (
    <Layout.Rail data-testid="cost-change-insights-rail">
      <Card className="hc-pa-normal hc-mt-2x hesp-card-border hesp-card-background">
        <div className="hc-fs-lg">
          <Grid.Item className="hc-pa-none hesp-general-fullwidth">
            Items
            <div className="hesp-position-relative">
              <>
                {filterGroupHasValues(COST_CHANGE_INSIGHTS_ITEM_OPTIONS) ? (
                  <>
                    {Object.values(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).map(
                      (itemField) => {
                        if (searchCriteria?.[itemField.variableName]?.length) {
                          return (
                            <Chip
                              data-testid={itemField.variableName + '-chip'}
                              key={itemField.variableName + '-chip'}
                              className="hesp-chip-plain hesp-appliedfilters-chip"
                              onClick={() => onFilterPopupOpen(itemField)}
                              onRequestDelete={(event) => {
                                event.stopPropagation()
                                removeValue(
                                  [],
                                  itemField.variableName,
                                  [],
                                  true,
                                )
                              }}
                            >
                              {searchCriteria?.[itemField.variableName]
                                ?.length === 1 ? (
                                <>{`${itemField.label} = ${searchCriteria?.[itemField.variableName][0]}`}</>
                              ) : (
                                <>{`${searchCriteria?.[itemField.variableName]?.length} ${itemField.pluralLabel}`}</>
                              )}
                            </Chip>
                          )
                        }
                        return null
                      },
                    )}
                  </>
                ) : (
                  <>
                    <Chip
                      data-testid="item-chip"
                      className="hesp-chip-plain hesp-appliedfilters-chip"
                    >
                      All Items
                    </Chip>
                  </>
                )}
              </>
            </div>
          </Grid.Item>
          <Grid.Item className="hc-pa-none hesp-general-fullwidth">
            Locations
            <div className="hesp-position-relative">
              <>
                {filterGroupHasValues(COST_CHANGE_INSIGHTS_LOCATION_OPTIONS) &&
                searchCriteria?.[
                  COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                    .COST_LOCATION_GROUP_VALUES.variableName
                ].length ? (
                  <>
                    <Chip
                      data-testid={
                        COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                          .COST_LOCATION_GROUP_VALUES.variableName + '-chip'
                      }
                      className="hesp-chip-plain hesp-appliedfilters-chip"
                      onClick={() =>
                        onFilterPopupOpen(
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES,
                        )
                      }
                      onRequestDelete={(event) => {
                        event.stopPropagation()
                        removeValue(
                          [],
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_VALUES.variableName,
                          [],
                          true,
                        )
                      }}
                    >
                      {searchCriteria?.[
                        COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                          .COST_LOCATION_GROUP_VALUES.variableName
                      ]?.length === 1 ? (
                        <>{`${ALL_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[searchCriteria?.[COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE.variableName]]?.label} = ${searchCriteria?.[COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES.variableName][0]}`}</>
                      ) : (
                        <>{`${searchCriteria?.[COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES.variableName]?.length} ${ALL_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[searchCriteria?.[COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE.variableName]].label}s`}</>
                      )}
                    </Chip>
                  </>
                ) : (
                  <>
                    <Chip
                      data-testid="locations-chip"
                      className="hesp-chip-plain hesp-appliedfilters-chip"
                      onRequestDelete={
                        searchCriteria?.[
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_TYPE.variableName
                        ]
                          ? (event) => {
                              event.stopPropagation()
                              removeValue(
                                [],
                                COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                                  .COST_LOCATION_GROUP_TYPE.variableName,
                                [],
                                true,
                              )
                            }
                          : null
                      }
                    >
                      {`${
                        searchCriteria?.[
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_TYPE.variableName
                        ]
                          ? ALL_COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                              searchCriteria?.[
                                COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                                  .COST_LOCATION_GROUP_TYPE.variableName
                              ]
                            ].label
                          : 'All Locations'
                      }`}
                    </Chip>
                  </>
                )}
              </>
            </div>
          </Grid.Item>
          <Grid.Item className="hc-pa-none hesp-general-fullwidth">
            Timeframes
            <div className="hesp-position-relative">
              <>
                {filterGroupHasValues(
                  COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
                ) ? (
                  <>
                    {Object.values(COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS).map(
                      (timeframeField) => {
                        if (searchCriteria?.[timeframeField.variableName]) {
                          return (
                            <div className="hc-ml-sm">
                              <span className="hc-fs-sm">
                                {timeframeField.pluralLabel}
                              </span>
                              <Chip
                                data-testid={
                                  timeframeField.variableName + '-chip'
                                }
                                key={timeframeField.variableName + '-chip'}
                                className="hesp-chip-plain hesp-appliedfilters-chip hesp-cv-filters-wrapped-chip"
                                onRequestDelete={(event) => {
                                  event.stopPropagation()
                                  removeValue(
                                    [],
                                    timeframeField.variableName,
                                    [],
                                    true,
                                  )
                                }}
                              >
                                <>{`${searchCriteria?.[timeframeField.variableName]?.startDate}-${searchCriteria?.[timeframeField.variableName]?.endDate}`}</>
                              </Chip>
                            </div>
                          )
                        } else {
                          return null
                        }
                      },
                    )}
                  </>
                ) : (
                  <>
                    <Chip
                      data-testid="timeframe-chip"
                      className="hesp-chip-plain hesp-appliedfilters-chip"
                    >
                      All Time
                    </Chip>
                  </>
                )}
              </>
            </div>
          </Grid.Item>
          <Grid.Item className="hc-pa-none hesp-general-fullwidth">
            Miscellaneous
            <div className="hesp-position-relative">
              <>
                {filterGroupHasValues(COST_CHANGE_INSIGHTS_MISC_OPTIONS) ? (
                  <>
                    {Object.values(COST_CHANGE_INSIGHTS_MISC_OPTIONS).map(
                      (miscField) => {
                        if (searchCriteria?.[miscField.variableName]?.length) {
                          return (
                            <Chip
                              data-testid={miscField.variableName + '-chip'}
                              key={miscField.variableName + '-chip'}
                              className="hesp-chip-plain hesp-appliedfilters-chip"
                              onClick={
                                Array.isArray(
                                  COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS[
                                    miscField.variableName
                                  ],
                                )
                                  ? () => onFilterPopupOpen(miscField)
                                  : null
                              }
                              onRequestDelete={(event) => {
                                event.stopPropagation()
                                removeValue(
                                  [],
                                  miscField.variableName,
                                  [],
                                  true,
                                )
                              }}
                            >
                              {Array.isArray(
                                COST_CHANGE_INSIGHTS_DEFAULT_SELECTIONS[
                                  miscField.variableName
                                ],
                              ) ? (
                                searchCriteria?.[miscField.variableName]
                                  ?.length === 1 ? (
                                  <>{`${miscField.label} = ${searchCriteria?.[miscField.variableName][0]}`}</>
                                ) : (
                                  <>{`${searchCriteria?.[miscField.variableName]?.length} ${miscField.pluralLabel}`}</>
                                )
                              ) : (
                                <>{`${miscField.label} = ${searchCriteria?.[miscField.variableName]}`}</>
                              )}
                            </Chip>
                          )
                        }
                        return null
                      },
                    )}
                  </>
                ) : (
                  <>
                    <Chip
                      data-testid="misc-chip"
                      className="hesp-chip-plain hesp-appliedfilters-chip"
                    >
                      None Selected
                    </Chip>
                  </>
                )}
              </>
            </div>
          </Grid.Item>
        </div>
        <FilterPopup
          data-testid="cost-change-insights-filters-popup"
          fieldConstant={popupFieldConstant}
          headingText={
            Object.keys(COST_CHANGE_INSIGHTS_LOCATION_OPTIONS).includes(
              popupFieldConstant?.keyName,
            )
              ? getFilterPopupLocationText()
              : null
          }
          isVisible={openPopup}
          values={searchCriteria?.[popupFieldConstant?.variableName]}
          onRefuse={onFilterPopupClose}
          onRemove={removeValue}
          removeChip={true}
        />
      </Card>
    </Layout.Rail>
  )
}
